import React, { useEffect, useRef, useState } from "react";
import { FaCog } from "react-icons/fa";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { apihubPost } from "../../api/apihub";

function readJwtPayload(token) {
  try {
    const part = String(token || "").split(".")[1];
    if (!part) return null;
    const padded = part.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

function configPath(projectId, kind) {
  const segment =
    kind === "sheet" ? "googlesheetintegration" : "googleformconfig";
  return `/businessprojects/${projectId}/${segment}`;
}

const GoogleOauthCallback = () => {
  const { projectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const oauthKind =
    readJwtPayload(searchParams.get("state"))?.kind === "sheet"
      ? "sheet"
      : "form";
  const [statusText, setStatusText] = useState("Finishing Google sign-in…");
  const [error, setError] = useState("");
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    let cancelled = false;

    async function finish() {
      const oauthError = searchParams.get("error");
      const code = searchParams.get("code");
      const state = searchParams.get("state");
      const kind =
        readJwtPayload(state)?.kind === "sheet" ? "sheet" : "form";
      const resultKey = kind === "sheet" ? "googleSheet" : "googleForm";
      const failLabel =
        kind === "sheet" ? "Failed to create Google Sheet." : "Failed to create Google Form.";
      const goToConfig = (result) => {
        if (cancelled) return;
        navigate(configPath(projectId, kind), {
          replace: true,
          state: result,
        });
      };

      if (oauthError) {
        goToConfig({
          [resultKey]: oauthError === "access_denied" ? "denied" : "error",
          message: oauthError === "access_denied" ? "" : oauthError,
        });
        return;
      }

      if (!code || !state) {
        setError("Missing authorization code.");
        setStatusText("Google sign-in did not finish.");
        return;
      }

      try {
        setStatusText(
          kind === "sheet"
            ? "Creating your Google Sheet…"
            : "Creating your Google Form…",
        );
        const res = await apihubPost(
          kind === "sheet"
            ? "/getInteractedCustomerDetail/googlesheetoauthcallback"
            : "/getInteractedCustomerDetail/googleformoauthcallback",
          { code, state },
        );
        if (cancelled) return;
        if (res?.success === false) {
          goToConfig({
            [resultKey]: res.status || "error",
            message: res.message || failLabel,
          });
          return;
        }
        goToConfig({
          [resultKey]: res?.status || "created",
          message: res?.message || "",
        });
      } catch (err) {
        if (cancelled) return;
        goToConfig({
          [resultKey]: "error",
          message: err.message || failLabel,
        });
      }
    }

    finish();
    return () => {
      cancelled = true;
    };
  }, [navigate, projectId, searchParams]);

  return (
    <div className="biz-panel-page biz-smsconfig-page">
      <div className="biz-panel-header biz-panel-header-row">
        <h1>
          <FaCog />
          {oauthKind === "sheet"
            ? "Google Sheet Integration"
            : "Google Form Config"}
        </h1>
      </div>
      {error ? (
        <p className="biz-banner biz-banner-error">{error}</p>
      ) : (
        <p className="biz-table-message">{statusText}</p>
      )}
      {error && (
        <p>
          <button
            type="button"
            className="biz-btn-primary"
            onClick={() =>
              navigate(configPath(projectId, oauthKind) || location.pathname, {
                replace: true,
              })
            }
          >
            {oauthKind === "sheet"
              ? "Back to Google Sheet Integration"
              : "Back to Google Form Config"}
          </button>
        </p>
      )}
    </div>
  );
};

export default GoogleOauthCallback;
